// Разбор замечаний ведомости. Сценарий 34.
//
// Панель показывает ровно тех людей, у кого сервер поставил пометку, и НИКОГО больше. Строка с
// нулевым итогом от равенства начислений официальной части сюда не попадает: это штатное дело,
// а не замечание (сценарий 84). Общий признак «итог нулевой» превратил бы список в шум на
// 281 строке, и его перестали бы читать — вместе с настоящими замечаниями.
//
// Каждая строка ведёт в разбор каскада по этому человеку: замечание без места, где его
// исправлять, — это половина ответа.

import { useLocation, useNavigate } from 'react-router';

import { RiCheckboxCircleLine } from '@remixicon/react';

import { formatKopecks } from '@/shared/money/format';
import { buildSearch, parseSearch, pushPanel } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { ErrorState, Skeleton } from '@/shared/ui/screen-state';

import { PANEL_SHEET_PERSON } from '../../route';
import { buildReview, требуетРазбора } from '../ReviewBar';

import { useSheetFromScreen } from './context';

export function ReviewPanel({ id }: { id: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { grid, error, retry, nothing } = useSheetFromScreen();

  if (nothing !== null) {
    // Вечная загрузка запрещена: выключенный запрос навсегда остаётся «в ожидании», и панель
    // без этой развилки крутила бы скелет, ничего не собираясь показать.
    return <div className='text-paragraph-sm text-text-sub-600'>{nothing}</div>;
  }
  if (error !== null && error !== undefined) {
    return <ErrorState subject='замечания по ведомости' error={error as never} onRetry={retry} />;
  }
  if (grid === undefined) {
    return (
      <div className='flex flex-col gap-2'>
        <Skeleton className='h-6 w-2/3 rounded-md' />
        <Skeleton className='h-40 w-full rounded-lg' />
      </div>
    );
  }
  if (grid.payroll.id !== id) {
    return (
      <div className='text-paragraph-sm text-text-sub-600'>
        Ссылка ведёт в другую ведомость, а на экране открыта эта. Выберите нужную слева и откройте
        разбор заново.
      </div>
    );
  }

  const проверка = buildReview(
    grid.employees,
    grid.totals,
    grid.calculated,
    grid.payroll.rows_without_rate,
    grid.payroll.official_period_id != null,
  );
  // Тот же предикат, что и у полосы: список обязан совпадать с её числом до человека.
  const сПометками = (grid.employees ?? []).filter(требуетРазбора);

  function открыть(employeeId: string) {
    const состояние = parseSearch(location.search);
    void navigate({
      pathname: location.pathname,
      search: buildSearch(pushPanel(состояние, { view: PANEL_SHEET_PERSON, id: employeeId })),
    });
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-0.5'>
        <span className='text-label-md text-text-strong-950'>
          {grid.payroll.site_name ?? 'Аппарат управления'}
        </span>
        <span className='text-paragraph-sm text-text-sub-600'>
          {grid.payroll.period_title} · {grid.payroll.status_title}
        </span>
      </div>

      {!проверка.calculated ? (
        <p className='text-paragraph-sm text-text-sub-600'>
          Расчёт по этой ведомости ещё не проводился, поэтому проверять пока нечего. Это не
          «замечаний нет»: суммы в колонках сейчас неизвестны, а не равны нулю.
        </p>
      ) : сПометками.length === 0 && проверка.officialUnlinked ? (
        // Людей с пометками нет, но и «замечаний нет» сказать нельзя: официальный период не
        // связан, и официальная часть не вычтена ни у кого (сверка 23.09.2026).
        <p className='rounded-lg bg-bg-weak-50 px-4 py-3 text-paragraph-sm text-text-strong-950'>
          Официальный период за этот месяц не связан с ведомостью: официальная часть не вычтена, и к
          выплате у тех, кто оформлен официально, завышено на неё. Заведите или загрузите
          официальный период на вкладке «Официально» в разделе «Расчёт» и пересчитайте.
        </p>
      ) : сПометками.length === 0 ? (
        <div className='flex items-start gap-2 rounded-lg bg-bg-weak-50 px-4 py-3'>
          <RiCheckboxCircleLine className='mt-0.5 size-4 shrink-0 text-success-base' />
          <div className='flex flex-col gap-0.5'>
            <span className='text-paragraph-sm text-text-strong-950'>Замечаний нет</span>
            <span className='text-paragraph-xs text-text-sub-600'>
              Проверено{' '}
              {countLabel(grid.totals.employees_count, ['человек', 'человека', 'человек'])}: итогов,
              обнулённых каскадом, людей без строки в официальном периоде и непосчитанных строк не
              нашлось. Строки с нулевым итогом от равенства начислений официальной части замечанием
              не считаются и сюда не выносятся.
            </span>
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-3'>
          <span className='text-label-sm text-text-strong-950'>
            Требуют разбора: {countLabel(сПометками.length, ['человек', 'человека', 'человек'])}
          </span>
          {/* Причина называется ОДИН раз, а под ней — все, кого она касается.
              На живом экране 25.08.2026 панель писала одну и ту же фразу «часы не заполнены
              в 1 строке: в расчёт она не вошла, начислено по ней ноль. Закройте табель объекта
              за этот месяц и повторите пересчёт» — дословно, четыре раза подряд, по разу на
              человека. Причина у всех была одна, а работа по ней одна на всех: закрыть табель.
              Сгруппированный список к тому же сразу показывает, КТО отличается: у первого
              человека к тем же часам добавлялся непогашенный долг после увольнения. */}
          {поПричинам(сПометками).map((группа) => (
            <div key={группа.ключ} className='flex flex-col gap-1'>
              <div className='flex flex-col gap-0.5'>
                {группа.причины.map((причина) => (
                  <span key={причина} className='text-paragraph-sm text-text-sub-600'>
                    {причина}
                  </span>
                ))}
              </div>
              <ul className='flex flex-col rounded-xl ring-1 ring-inset ring-stroke-soft-200'>
                {группа.люди.map((человек) => (
                  <li key={человек.id} className='border-b border-stroke-soft-200 last:border-b-0'>
                    <button
                      type='button'
                      onClick={() => открыть(человек.id)}
                      className='flex w-full items-baseline justify-between gap-2 px-4 py-2 text-left transition duration-200 ease-out hover:bg-bg-weak-50'
                    >
                      <span className='truncate text-paragraph-sm text-text-strong-950'>
                        {человек.employee_name}
                      </span>
                      {/* Расчёта не было — сумма НЕИЗВЕСТНА, а не равна нулю. В сетке по этим
                          же людям стоит прочерк, и «0,00» здесь спорило бы с ней на одном
                          экране. */}
                      <span className='shrink-0 tabular-nums text-label-sm text-text-strong-950'>
                        {человек.calculated_at === null ? '—' : formatKopecks(человек.total_payout)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {проверка.excludedRows > 0 && (
        <p className='text-paragraph-xs text-text-sub-600'>
          Строк выведено из расчёта с основанием: {проверка.excludedRows}. Это штатное дело и
          замечанием не считается — основание видно прямо в строке, в колонке поста.
        </p>
      )}
    </div>
  );
}

/**
 * Что сказать про человека в разборе.
 *
 * Обычно это готовые пометки сервера. Но человек попадает сюда и по флагу без пометки, и по
 * отсутствию расчёта — тогда строка без единого слова читалась бы как «непонятно зачем он тут».
 */
function пометки(человек: { review_notes: string[]; calculated_at: string | null }): string[] {
  if (человек.review_notes.length > 0) {
    return человек.review_notes;
  }
  if (человек.calculated_at === null) {
    return ['расчёта по этому человеку ещё не было: суммы в его строке неизвестны, а не нулевые'];
  }
  // Запасной текст. Он не обвиняет разработчиков и не оставляет человека без следующего шага:
  // раньше здесь стояло «покажите разработчикам», и на живом экране это читали четверо из
  // пяти — потому что признак «нет в официальном периоде» хранится своей колонкой и до
  // пометок не доходил. Причину починили на сервере, но запасной текст всё равно обязан быть
  // человеческим: он появляется там, где система чего-то про строку не знает.
  return ['строка помечена к разбору, а причина не сохранилась. Нажмите «Пересчитать»'];
}

/**
 * Люди, сгруппированные по СОВПАДАЮЩЕМУ набору причин.
 *
 * Порядок групп — по числу людей, крупные первыми: с них и начинают, потому что одна работа
 * снимает сразу многих. Внутри группы порядок сохраняется тот, что пришёл с сервера.
 */
function поПричинам(
  люди: readonly {
    id: string;
    employee_name: string;
    total_payout: number;
    review_notes: string[];
    calculated_at: string | null;
  }[],
): { ключ: string; причины: string[]; люди: typeof люди }[] {
  const группы = new Map<string, { причины: string[]; люди: (typeof люди)[number][] }>();
  for (const человек of люди) {
    const причины = пометки(человек);
    const ключ = причины.join('\u0000');
    const группа = группы.get(ключ);
    if (группа === undefined) {
      группы.set(ключ, { причины, люди: [человек] });
    } else {
      группа.люди.push(человек);
    }
  }
  return [...группы.entries()]
    .map(([ключ, группа]) => ({ ключ, причины: группа.причины, люди: группа.люди }))
    .sort((левая, правая) => правая.люди.length - левая.люди.length);
}
